import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../Config/axiosInstance";
import DistributionForm from "./DistributionForm";
import ReviewForm from "./ReviewForm";
import "./AddAutovehicles.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import Select from 'react-select';
import { NumericFormat } from "react-number-format";

const AddAutovehicles = () => {
    const navigate = useNavigate();

    // tipuri combustibil - mutat în useMemo
    const fuelTypes = useMemo(() => [
        "Benzina",
        "Motorina",
        "GPL",
        "Hybrid",
        "Electric",
    ], []);

    // opțiuni pentru status - mutat în useMemo
    const statusOptions = useMemo(() => [
        "Open",
        "Closed"
    ], []);

    // formular - fără expire aici (mutat în contract)
    const initialFormData = useMemo(() => ({
        auto: "",
        auto_type: "",
        driver: "",
        registration_number: "",
        RCA: "",
        RCA_display: "",
        ITP: "",
        ITP_display: "",
        E_Vignette: "",
        E_Vignette_display: "",
        VIN_series: "",
        year: "",
        cylindrical_capacity: "",
        combustibile: "",
    }), []);

    // formular pentru contract - cu expire adăugat
    const initialContractData = useMemo(() => ({
        supplier: "",
        signing_date: "",
        signing_date_display: "",
        duration: "",
        start_date: "",
        start_date_display: "",
        expire: "", // Mutat aici din formData
        expire_display: "", // Mutat aici din formData
        status: "Open",
        advance_payment: "",
        monthly_installment: "",
        value: "",
        annual_mileage: "",
        replacement_days: "",
        km_pickup: "",
        km_return: "",
    }), []);

    // state-uri principale
    const [formData, setFormData] = useState(initialFormData);
    const [contractData, setContractData] = useState(initialContractData);
    const [drivers, setDrivers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [contractFieldErrors, setContractFieldErrors] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Adaugă acest state nou pentru a controla validarea
    const [isSubmitted, setIsSubmitted] = useState(false);

    // state pentru distribuție și revizie (doar un set de date)
    const [distribution, setDistribution] = useState({
        distribution_data: "",
        distribution_km: "",
        distribution_interval: "",
        estimate_date_distribution: "",
        estimate_km_distribution: "",
    });

    const [review, setReview] = useState({
        review_data: "",
        review_km: "",
        review_interval: "",
        estimate_date_review: "",
        estimate_km_review: "",
    });

    // state pentru kilometraj
    const [kilometers, setKilometers] = useState({
        kilometers: "",
        recorded_date: format(new Date(), "yyyy-MM-dd"),
        recorded_date_display: format(new Date(), "dd.MM.yyyy"),
    });

    // încarcă lista șoferilor la montare
    useEffect(() => {
        axiosInstance.get("/employees/")
            .then((res) => setDrivers(res.data))
            .catch(() => console.error("Failed to load employees"));
    }, []);

    // Obține departamentele permise și apoi supplierii
    useEffect(() => {
        // Obține departamentele permise
        axiosInstance.get("/department/user/")
            .then((response) => {
                const allowedDeptIds = response.data.map(d => d.value);

                // Obține toți supplierii și îi filtrează
                axiosInstance.get("/suppliers/")
                    .then((res) => {
                        let filteredSuppliers = res.data;

                        if (allowedDeptIds.length > 0) {
                            filteredSuppliers = filteredSuppliers.filter(supplier =>
                                supplier.departments?.some(deptId => allowedDeptIds.includes(deptId))
                            );
                        }

                        setSuppliers(filteredSuppliers);
                    })
                    .catch(() => console.error("Failed to load suppliers"));
            })
            .catch((error) => console.error("Failed to load allowed departments:", error));
    }, []);

    // Opțiuni pentru react-select din array-ul drivers
    const driverOptions = useMemo(() =>
        drivers.map(driver => ({
            value: driver.id,
            label: `${driver.first_name} ${driver.last_name}`
        })), [drivers]);

    // Opțiuni pentru react-select din array-ul suppliers
    const supplierOptions = useMemo(() =>
        suppliers.map(supplier => ({
            value: supplier.id,
            label: supplier.name
        })), [suppliers]);

    // Opțiuni pentru combustibil
    const fuelOptions = useMemo(() =>
        fuelTypes.map(fuel => ({
            value: fuel,
            label: fuel
        })), [fuelTypes]);

    // Opțiuni pentru status
    const statusSelectOptions = useMemo(() =>
        statusOptions.map(status => ({
            value: status,
            label: status
        })), [statusOptions]);

    // Stiluri personalizate pentru react-select care imită AddCostModal
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            width: '100%',
            height: '38px',
            border: state.isFocused ? '2px solid #050d15' : '1.5px solid #ccc',
            borderRadius: '8px',
            backgroundColor: 'white',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(0, 0, 0, 0.2)' : 'none',
            // Elimină hover-ul
            '&:hover': {
                borderColor: state.isFocused ? '#050d15' : '1.5px solid #ccc',
            },
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            minHeight: '38px',
            cursor: 'pointer',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#777',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1a1a1a',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            fontSize: '14px',
            color: '#1a1a1a',
            fontFamily: "'Arial', sans-serif",
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 12px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '36px',
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? '#1976d2' : '#555',
            padding: '8px',
            '&:hover': {
                color: '#1976d2',
            }
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#ddd',
            marginTop: '8px',
            marginBottom: '8px',
            // Asigură că indicatorSeparator este întotdeauna vizibil
            display: 'block !important',
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 9999,
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '4px 0',
            maxHeight: '200px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#1976d2' :
                state.isFocused ? '#f0f7ff' : 'white',
            color: state.isSelected ? 'white' : '#1a1a1a',
            padding: '8px 12px',
            fontSize: '14px',
            fontFamily: "'Arial', sans-serif",
            cursor: 'pointer',
            // Elimină hover-ul pe opțiuni când sunt selectate
            '&:hover': {
                backgroundColor: state.isSelected ? '#1976d2' : '#f0f7ff',
            },
            '&:active': {
                backgroundColor: '#1976d2',
                color: 'white'
            }
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: '#666',
            padding: '8px',
            // Elimină hover-ul pe butonul de clear
            '&:hover': {
                color: '#666',
            }
        }),
    };

    // Stil pentru select-uri goale (cu placeholder) - actualizează pentru indicatorSeparator
    const emptySelectStyles = {
        ...customSelectStyles,
        singleValue: (provided) => ({
            ...provided,
            color: '#777 !important',
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#ddd',
            marginTop: '8px',
            marginBottom: '8px',
            // Asigură că indicatorSeparator este întotdeauna vizibil și pentru empty
            display: 'block !important',
        }),
    };

    // Stil pentru select-uri cu eroare
    const errorSelectStyles = {
        ...customSelectStyles,
        control: (provided, state) => ({
            ...customSelectStyles.control(provided, state),
            border: '1px solid #ff5c5c !important',
            boxShadow: state.isFocused ? '0 0 0 1px rgba(255, 92, 92, 0.2)' : '0 0 0 1px rgba(255, 92, 92, 0.5)',
            // Elimină hover-ul pentru erori
            '&:hover': {
                borderColor: state.isFocused ? '#ff5c5c' : '#ff5c5c !important',
            }
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: '#ddd',
            marginTop: '8px',
            marginBottom: '8px',
            // Asigură că indicatorSeparator este întotdeauna vizibil și pentru erori
            display: 'block !important',
        })
    };

    // Handler pentru react-select pentru driver
    const handleDriverSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            driver: selectedOption ? selectedOption.value : ""
        }));
        // Șterge eroarea doar dacă formularul a fost deja submis
        if (isSubmitted) {
            setFieldErrors(prev => ({ ...prev, driver: "" }));
        }
    };

    // Handler pentru react-select pentru supplier
    const handleSupplierSelect = (selectedOption) => {
        setContractData(prev => ({
            ...prev,
            supplier: selectedOption ? selectedOption.value : ""
        }));
        // Șterge eroarea doar dacă formularul a fost deja submis
        if (isSubmitted) {
            setContractFieldErrors(prev => ({ ...prev, supplier: "" }));
        }
    };

    // Handler pentru react-select pentru combustibil
    const handleFuelSelect = (selectedOption) => {
        setFormData(prev => ({
            ...prev,
            combustibile: selectedOption ? selectedOption.value : ""
        }));
        // Șterge eroarea doar dacă formularul a fost deja submis
        if (isSubmitted) {
            setFieldErrors(prev => ({ ...prev, combustibile: "" }));
        }
    };

    // Handler pentru react-select pentru status
    const handleStatusSelect = (selectedOption) => {
        setContractData(prev => ({
            ...prev,
            status: selectedOption ? selectedOption.value : "Open"
        }));
    };

    // gestionează selectarea datelor din DatePicker pentru formularul principal
    const handleDatePick = (field, date) => {
        if (!date) {
            setFormData((prev) => ({
                ...prev,
                [field]: "",
                [field + "_display"]: ""
            }));

            // Setează eroare doar dacă formularul a fost deja submis
            if (isSubmitted) {
                setFieldErrors((prev) => ({ ...prev, [field]: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setFormData((prev) => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        // Șterge eroarea doar dacă formularul a fost deja submis
        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // gestionează selectarea datelor din DatePicker pentru contract
    const handleContractDatePick = (field, date) => {
        if (!date) {
            setContractData((prev) => ({
                ...prev,
                [field]: "",
                [field + "_display"]: ""
            }));

            // Setează eroare doar dacă formularul a fost deja submis
            if (isSubmitted && field === "expire") {
                setContractFieldErrors(prev => ({ ...prev, [field]: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setContractData((prev) => ({
            ...prev,
            [field]: iso,
            [field + "_display"]: display,
        }));

        // Clear error when date is selected (doar dacă a fost submis)
        if (isSubmitted) {
            setContractFieldErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    // gestionează selectarea datei pentru kilometraj
    const handleKilometersDatePick = (date) => {
        if (!date) {
            setKilometers((prev) => ({
                ...prev,
                recorded_date: "",
                recorded_date_display: ""
            }));

            // Setează eroare pentru recorded_date DOAR dacă formularul a fost deja submis
            if (isSubmitted) {
                setFieldErrors((prev) => ({ ...prev, recorded_date: "Required" }));
            }
            return;
        }

        const iso = format(date, "yyyy-MM-dd");
        const display = format(date, "dd.MM.yyyy");

        setKilometers((prev) => ({
            ...prev,
            recorded_date: iso,
            recorded_date_display: display,
        }));

        // Curăță eroarea pentru recorded_date DOAR dacă formularul a fost deja submis
        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, recorded_date: "" }));
        }
    };

    // update pentru câmpuri text (formular principal)
    const handleChange = (field) => (e) => {
        let value = e.target.value;

        if (field === "year") value = value.replace(/\D/g, "").slice(0, 4);
        if (field === "cylindrical_capacity") value = value.replace(/\D/g, "");

        setFormData((prev) => ({ ...prev, [field]: value }));

        // Șterge eroarea doar dacă formularul a fost deja submis
        if (isSubmitted) {
            setFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // update pentru câmpuri contract
    const handleContractChange = (field) => (e) => {
        let value = e.target.value;

        // Validare pentru câmpurile numerice (exceptând cele cu NumericFormat)
        if (field === "annual_mileage" || field === "replacement_days" ||
            field === "km_pickup" || field === "km_return" || field === "duration") {
            value = value.replace(/\D/g, ""); // Doar numere
        }

        setContractData((prev) => ({ ...prev, [field]: value }));

        // Șterge eroarea doar dacă formularul a fost deja submis
        if (isSubmitted) {
            setContractFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // Handler special pentru NumericFormat câmpuri
    const handleNumericChange = (field) => ({ value }) => {
        setContractData((prev) => ({ ...prev, [field]: value }));
        
        // Șterge eroarea doar dacă formularul a fost deja submis
        if (isSubmitted) {
            setContractFieldErrors((prev) => ({ ...prev, [field]: "" }));
        }
    };

    // update pentru câmpurile kilometrajului
    const handleKilometersChange = (field) => (e) => {
        let value = e.target.value;

        if (field === "kilometers") {
            value = value.replace(/\D/g, ""); // Doar numere pentru kilometri
        }

        setKilometers((prev) => ({ ...prev, [field]: value }));

        // Clear error if any (doar dacă formularul a fost deja submis)
        if (isSubmitted && fieldErrors.kilometers) {
            setFieldErrors((prev) => ({ ...prev, kilometers: "" }));
        }
    };

    // validează câmpurile obligatorii principale
    const validateMainForm = () => {
        const errors = {};

        const requiredFields = [
            "auto",
            "auto_type",
            "registration_number",
            "year",
            "VIN_series",
            "cylindrical_capacity",
            "RCA",
            "ITP",
            "E_Vignette",
            "combustibile",
        ];

        requiredFields.forEach((f) => {
            if (!formData[f] || formData[f].toString().trim() === "") {
                errors[f] = "Required";
            }
        });

        // Validare pentru kilometraj
        if (!kilometers.kilometers || kilometers.kilometers.toString().trim() === "") {
            errors.kilometers = "Required";
        }

        // Validare pentru data înregistrării kilometrajului
        if (!kilometers.recorded_date || kilometers.recorded_date.toString().trim() === "") {
            errors.recorded_date = "Required";
        }

        return errors;
    };

    // validează câmpurile obligatorii pentru contract
    const validateContractForm = () => {
        const errors = {};

        // Câmpurile obligatorii pentru contract (cu expire inclus acum)
        const requiredContractFields = [
            "supplier",
            "signing_date",
            "duration",
            "start_date",
            "expire", // Adăugat aici
            "advance_payment",
            "monthly_installment",
            "value",
            "annual_mileage",
            "replacement_days",
            "km_pickup",
            "km_return",
        ];

        requiredContractFields.forEach((f) => {
            const value = contractData[f];

            if (f === "advance_payment" || f === "monthly_installment" || f === "value") {
                // Convertim valoarea formatată pentru validare
                const numericValue = value ? parseFloat(value.replace(/\./g, '').replace(',', '.')) : null;
                if (!value || value.toString().trim() === "" || numericValue <= 0) {
                    errors[f] = "Required";
                }
            } else if (f === "annual_mileage" || f === "replacement_days" ||
                f === "km_pickup" || f === "km_return" || f === "duration") {
                if (!value || value.toString().trim() === "" || parseInt(value) < 0) {
                    errors[f] = "Required";
                }
            } else if (!value || value.toString().trim() === "") {
                errors[f] = "Required";
            }
        });

        return errors;
    };

    // orice string gol devine null 
    const cleanPayload = (obj) => {
        const out = {};
        for (const key in obj) {
            out[key] = obj[key] === "" ? null : obj[key];
        }
        return out;
    };

    // afișează eroarea în toast
    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorToast(true);
        setTimeout(() => setShowErrorToast(false), 5000);
    };

    // Helper pentru a afișa eroare pentru un câmp specific (doar dacă formularul a fost submis)
    const shouldShowError = (fieldName, errorObj) => {
        return isSubmitted && errorObj[fieldName];
    };

    // trimite vehiculul + distribuția + revizia + kilometri la backend
    const handleSubmit = async () => {
        // Setează că formularul a fost submis
        setIsSubmitted(true);

        // Validează și verifică dacă formularul este valid
        const mainErrors = validateMainForm();
        const contractErrors = validateContractForm();

        // Setează toate erorile
        setFieldErrors(mainErrors);
        setContractFieldErrors(contractErrors);

        // Verifică dacă există erori
        const hasErrors = Object.keys(mainErrors).length > 0 || Object.keys(contractErrors).length > 0;

        if (hasErrors) {
            // Dacă există erori, oprește salvarea
            showError("Please fill in all required fields");
            return;
        }

        setIsSaving(true);

        try {
            // Creăm payload-ul pentru vehicul (fără expire aici)
            const payload = {
                ...formData,
                driver: formData.driver ? parseInt(formData.driver) : null,
                year: formData.year ? `${formData.year}-01-01` : null,
                // Adăugăm datele contractului la payload (cu expire inclus)
                supplier: contractData.supplier ? parseInt(contractData.supplier) : null,
                signing_date: contractData.signing_date || null,
                duration: contractData.duration || null,
                start_date: contractData.start_date || null,
                expire: contractData.expire || null, // Expire vine din contractData acum
                status: contractData.status,
                advance_payment: contractData.advance_payment ? 
                    parseFloat(contractData.advance_payment.replace(/\./g, '').replace(',', '.')) : null,
                monthly_installment: contractData.monthly_installment ? 
                    parseFloat(contractData.monthly_installment.replace(/\./g, '').replace(',', '.')) : null,
                value: contractData.value ? 
                    parseFloat(contractData.value.replace(/\./g, '').replace(',', '.')) : null,
                annual_mileage: contractData.annual_mileage ? parseInt(contractData.annual_mileage) : null,
                replacement_days: contractData.replacement_days ? parseInt(contractData.replacement_days) : null,
                km_pickup: contractData.km_pickup ? parseInt(contractData.km_pickup) : null,
                km_return: contractData.km_return ? parseInt(contractData.km_return) : null,
            };

            const res = await axiosInstance.post("/all-autovehicles/", cleanPayload(payload));
            const vehicleId = res.data.id;

            // Salvează kilometrajul - endpoint separat
            if (kilometers.kilometers) {
                const kilometersPayload = {
                    ...cleanPayload(kilometers),
                    vehicle: vehicleId,
                };

                console.log("Kilometers payload:", kilometersPayload); // Pentru debugging

                await axiosInstance.post("/autovehicle-kilometers/", kilometersPayload);
            }

            // Salvează distribuția - doar dacă există date
            if (distribution.distribution_data || distribution.distribution_km) {
                await axiosInstance.post("/distribution/", {
                    ...cleanPayload(distribution),
                    vehicle: vehicleId,
                });
            }

            // Salvează revizia - doar dacă există date
            if (review.review_data || review.review_km) {
                await axiosInstance.post("/review/", {
                    ...cleanPayload(review),
                    vehicle: vehicleId,
                });
            }

            setShowToast(true);
            setTimeout(() => navigate("/all-autovehicles"), 1500);

        } catch (err) {
            setIsSaving(false);
            console.error("Error saving vehicle:", err);
            console.error("Error details:", err.response?.data);

            // Extrage mesajul de eroare din răspuns sau folosește unul generic
            let errorMsg = "An error occurred while saving the vehicle.";

            if (err.response?.data) {
                const errorData = err.response.data;

                if (typeof errorData === 'object') {
                    const errorMessages = Object.values(errorData).flat();
                    if (errorMessages.length > 0) {
                        errorMsg = errorMessages.join(', ');
                    }
                } else if (typeof errorData === 'string') {
                    errorMsg = errorData;
                }
            }

            showError(errorMsg);
        }
    };

    // Găsește valoarea selectată pentru driver
    const selectedDriver = driverOptions.find(option => option.value === formData.driver);
    // Găsește valoarea selectată pentru supplier
    const selectedSupplier = supplierOptions.find(option => option.value === contractData.supplier);
    // Găsește valoarea selectată pentru combustibil
    const selectedFuel = fuelOptions.find(option => option.value === formData.combustibile);
    // Găsește valoarea selectată pentru status
    const selectedStatus = statusSelectOptions.find(option => option.value === contractData.status);

    return (
        <>
            {showToast && <div className="global-toast success">✔ Autovehicle added successfully!</div>}
            {showErrorToast && <div className="global-toast error">✗ {errorMessage}</div>}

            <div className="addautovehicles-page">
                <div className="add-autovehicle-container">

                    {/* HEADER */}
                    <div className="top-header addauto-header">
                        <div className="header-left">
                            <ArrowBackIcon className="back-icon-autovehicles" onClick={() => navigate(-1)} />
                            <h2 className="header-title-autovehicles">New autovehicle</h2>
                        </div>

                        <div className="header-actions">
                            <button className="cancel-btn" onClick={() => navigate(-1)}>Cancel</button>
                            <button
                                className={`save-btn ${isSaving ? "disabled" : ""}`}
                                onClick={!isSaving ? handleSubmit : undefined}
                                disabled={isSaving}
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>

                    <hr className="header-divider-autovehicles" />

                    {/* FORM SECTIONS */}
                    <div className="form-sections">

                        {/* LEFT SIDE */}
                        <div className="vehicle-form">
                            <div className="form-card">

                                <div className="row1">
                                    <div className="field">
                                        <label>Auto <span className="required">*</span></label>
                                        <input
                                            value={formData.auto}
                                            placeholder="Auto name"
                                            onChange={handleChange("auto")}
                                            className={shouldShowError("auto", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("auto", fieldErrors) && <span className="field-error">{fieldErrors.auto}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Type <span className="required">*</span></label>
                                        <input
                                            value={formData.auto_type}
                                            placeholder="Auto type"
                                            onChange={handleChange("auto_type")}
                                            className={shouldShowError("auto_type", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("auto_type", fieldErrors) && <span className="field-error">{fieldErrors.auto_type}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Registration number <span className="required">*</span></label>
                                        <input
                                            value={formData.registration_number}
                                            placeholder="Ex: B 76 FGH"
                                            onChange={handleChange("registration_number")}
                                            className={shouldShowError("registration_number", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("registration_number", fieldErrors) && <span className="field-error">{fieldErrors.registration_number}</span>}
                                    </div>
                                </div>

                                <div className="row2">
                                    {/* Câmpul pentru kilometraj curent */}
                                    <div className="field">
                                        <label>Current Km <span className="required">*</span></label>
                                        <input
                                            value={kilometers.kilometers}
                                            placeholder="Ex: 120000"
                                            onChange={handleKilometersChange("kilometers")}
                                            className={shouldShowError("kilometers", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("kilometers", fieldErrors) && <span className="field-error">{fieldErrors.kilometers}</span>}
                                    </div>

                                    {/* Câmpul pentru data înregistrării kilometrajului - OBLIGATORIU */}
                                    <div className="field">
                                        <label>Km Recorded Date <span className="required">*</span></label>
                                        <div className="datepicker-container">
                                            <DatePicker
                                                id="datepicker-kilometers-date"
                                                selected={kilometers.recorded_date ? new Date(kilometers.recorded_date) : null}
                                                onChange={handleKilometersDatePick}
                                                dateFormat="dd.MM.yyyy"
                                                placeholderText="dd.mm.yyyy"
                                                className={`datepicker-input-with-icon ${shouldShowError("recorded_date", fieldErrors) ? "error-input" : ""}`}
                                                calendarClassName="custom-calendar"
                                                autoComplete="off"
                                            />
                                            <span
                                                className="calendar-icon-inside"
                                                onClick={() => document.querySelector("#datepicker-kilometers-date")?.focus()}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                </svg>
                                            </span>
                                        </div>
                                        {shouldShowError("recorded_date", fieldErrors) && <span className="field-error">{fieldErrors.recorded_date}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field">
                                        <label>Driver</label>
                                        <Select
                                            value={selectedDriver}
                                            onChange={handleDriverSelect}
                                            options={driverOptions}
                                            placeholder="Select driver"
                                            isSearchable
                                            isClearable={true}
                                            styles={!formData.driver ? emptySelectStyles : customSelectStyles}
                                            className={`react-select-container ${shouldShowError("driver", fieldErrors) ? 'select-error' : ''}`}
                                            classNamePrefix="react-select"
                                        />
                                        {shouldShowError("driver", fieldErrors) && <span className="field-error">{fieldErrors.driver}</span>}
                                    </div>
                                </div>

                                <div className="row1">
                                    <div className="field vin-full">
                                        <label>VIN series <span className="required">*</span></label>
                                        <input
                                            value={formData.VIN_series}
                                            placeholder="Ex: TMBDC23U989016367"
                                            onChange={handleChange("VIN_series")}
                                            className={shouldShowError("VIN_series", fieldErrors) ? "error-input" : ""}
                                        />
                                        {shouldShowError("VIN_series", fieldErrors) && <span className="field-error">{fieldErrors.VIN_series}</span>}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div className="right-side">
                            <div className="top-3-dates">

                                <div className="field">
                                    <label>Made year <span className="required">*</span></label>
                                    <input
                                        value={formData.year}
                                        maxLength={4}
                                        placeholder="Enter year (YYYY)"
                                        onChange={handleChange("year")}
                                        className={shouldShowError("year", fieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("year", fieldErrors) && <span className="field-error">{fieldErrors.year}</span>}
                                </div>

                                <div className="field">
                                    <label>Cylindrical capacity <span className="required">*</span></label>
                                    <input
                                        value={formData.cylindrical_capacity}
                                        placeholder="Engine capacity (cm³)"
                                        onChange={handleChange("cylindrical_capacity")}
                                        className={shouldShowError("cylindrical_capacity", fieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("cylindrical_capacity", fieldErrors) && <span className="field-error">{fieldErrors.cylindrical_capacity}</span>}
                                </div>

                                <div className="field">
                                    <label>Combustible <span className="required">*</span></label>
                                    <Select
                                        value={selectedFuel}
                                        onChange={handleFuelSelect}
                                        options={fuelOptions}
                                        placeholder="Select combustible type"
                                        isClearable={true}
                                        styles={shouldShowError("combustibile", fieldErrors) ? errorSelectStyles : customSelectStyles}
                                        className={`react-select-container ${shouldShowError("combustibile", fieldErrors) ? 'select-error' : ''}`}
                                        classNamePrefix="react-select"
                                    />
                                    {shouldShowError("combustibile", fieldErrors) && (
                                        <span className="field-error">{fieldErrors.combustibile}</span>
                                    )}
                                </div>

                                <div className="field">
                                    <label>RCA <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-rca"
                                            selected={formData.RCA ? new Date(formData.RCA) : null}
                                            onChange={(date) => handleDatePick("RCA", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("RCA", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-rca")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("RCA", fieldErrors) && <span className="field-error">{fieldErrors.RCA}</span>}
                                </div>

                                <div className="field">
                                    <label>ITP <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-itp"
                                            selected={formData.ITP ? new Date(formData.ITP) : null}
                                            onChange={(date) => handleDatePick("ITP", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("ITP", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-itp")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("ITP", fieldErrors) && <span className="field-error">{fieldErrors.ITP}</span>}
                                </div>

                                <div className="field">
                                    <label>E-Vignette <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-vignette"
                                            selected={formData.E_Vignette ? new Date(formData.E_Vignette) : null}
                                            onChange={(date) => handleDatePick("E_Vignette", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("E_Vignette", fieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-vignette")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("E_Vignette", fieldErrors) && <span className="field-error">{fieldErrors.E_Vignette}</span>}
                                </div>

                            </div>

                            <div className="bottom-forms">
                                <DistributionForm
                                    data={distribution}
                                    setData={setDistribution}
                                    editMode={true}
                                />
                                <ReviewForm
                                    data={review}
                                    setData={setReview}
                                    editMode={true}
                                />
                            </div>
                        </div>
                    </div>

                    {/* CONTRACT FORM - CU EXPIRE ADAUGAT */}
                    <div className="contract-form-section">
                        <h3 className="contract-title">Contract Information</h3>

                        <div className="contract-form-grid">
                            {/* ROW 1 - SUPPLIER FULL WIDTH */}
                            <div className="contract-row-full">
                                <div className="field full-width">
                                    <label>Supplier <span className="required">*</span></label>
                                    <Select
                                        value={selectedSupplier}
                                        onChange={handleSupplierSelect}
                                        options={supplierOptions}
                                        placeholder="Select supplier"
                                        isSearchable
                                        isClearable={true}
                                        styles={shouldShowError("supplier", contractFieldErrors) ? errorSelectStyles : customSelectStyles}
                                        className={`react-select-container ${shouldShowError("supplier", contractFieldErrors) ? 'select-error' : ''}`}
                                        classNamePrefix="react-select"
                                    />
                                    {shouldShowError("supplier", contractFieldErrors) && <span className="field-error">{contractFieldErrors.supplier}</span>}
                                </div>
                            </div>

                            {/* ROW 2 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Signing Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-signing"
                                            selected={contractData.signing_date ? new Date(contractData.signing_date) : null}
                                            onChange={(date) => handleContractDatePick("signing_date", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("signing_date", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-signing")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("signing_date", contractFieldErrors) && <span className="field-error">{contractFieldErrors.signing_date}</span>}
                                </div>

                                <div className="field">
                                    <label>Duration <span className="required">*</span></label>
                                    <input
                                        value={contractData.duration}
                                        placeholder="Ex: 36 months"
                                        onChange={handleContractChange("duration")}
                                        className={shouldShowError("duration", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("duration", contractFieldErrors) && <span className="field-error">{contractFieldErrors.duration}</span>}
                                </div>

                                <div className="field">
                                    <label>Start Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-start"
                                            selected={contractData.start_date ? new Date(contractData.start_date) : null}
                                            onChange={(date) => handleContractDatePick("start_date", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("start_date", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-start")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("start_date", contractFieldErrors) && <span className="field-error">{contractFieldErrors.start_date}</span>}
                                </div>
                            </div>

                            {/* ROW 3 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Expire Date <span className="required">*</span></label>
                                    <div className="datepicker-container">
                                        <DatePicker
                                            id="datepicker-expire"
                                            selected={contractData.expire ? new Date(contractData.expire) : null}
                                            onChange={(date) => handleContractDatePick("expire", date)}
                                            dateFormat="dd.MM.yyyy"
                                            placeholderText="dd.mm.yyyy"
                                            className={`datepicker-input-with-icon ${shouldShowError("expire", contractFieldErrors) ? "error-input" : ""}`}
                                            calendarClassName="custom-calendar"
                                            autoComplete="off"
                                        />
                                        <span
                                            className="calendar-icon-inside"
                                            onClick={() => document.querySelector("#datepicker-expire")?.focus()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        </span>
                                    </div>
                                    {shouldShowError("expire", contractFieldErrors) && <span className="field-error">{contractFieldErrors.expire}</span>}
                                </div>

                                <div className="field">
                                    <label>Status <span className="required">*</span></label>
                                    <Select
                                        value={selectedStatus}
                                        onChange={handleStatusSelect}
                                        options={statusSelectOptions}
                                        placeholder="Select status"
                                        isSearchable
                                        styles={customSelectStyles}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                <div className="field">
                                    <label>Advance Payment (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.advance_payment}
                                        onValueChange={handleNumericChange("advance_payment")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("advance_payment", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                    />
                                    {shouldShowError("advance_payment", contractFieldErrors) && <span className="field-error">{contractFieldErrors.advance_payment}</span>}
                                </div>
                            </div>

                            {/* ROW 4 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Monthly Installment (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.monthly_installment}
                                        onValueChange={handleNumericChange("monthly_installment")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("monthly_installment", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                    />
                                    {shouldShowError("monthly_installment", contractFieldErrors) && <span className="field-error">{contractFieldErrors.monthly_installment}</span>}
                                </div>

                                <div className="field">
                                    <label>Contract Value (LEI) <span className="required">*</span></label>
                                    <NumericFormat
                                        value={contractData.value}
                                        onValueChange={handleNumericChange("value")}
                                        thousandSeparator="."
                                        decimalSeparator=","
                                        decimalScale={2}
                                        fixedDecimalScale
                                        allowNegative={false}
                                        allowLeadingZeros={false}
                                        inputMode="decimal"
                                        className={`input ${shouldShowError("value", contractFieldErrors) ? "error-input" : ""}`}
                                        placeholder="0,00"
                                        required
                                    />
                                    {shouldShowError("value", contractFieldErrors) && <span className="field-error">{contractFieldErrors.value}</span>}
                                </div>

                                <div className="field">
                                    <label>Annual Km <span className="required">*</span></label>
                                    <input
                                        value={contractData.annual_mileage}
                                        placeholder="0"
                                        onChange={handleContractChange("annual_mileage")}
                                        className={shouldShowError("annual_mileage", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("annual_mileage", contractFieldErrors) && <span className="field-error">{contractFieldErrors.annual_mileage}</span>}
                                </div>
                            </div>

                            {/* ROW 5 - 3 COLUMNS */}
                            <div className="contract-row">
                                <div className="field">
                                    <label>Replacement Days <span className="required">*</span></label>
                                    <input
                                        value={contractData.replacement_days}
                                        placeholder="0"
                                        onChange={handleContractChange("replacement_days")}
                                        className={shouldShowError("replacement_days", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("replacement_days", contractFieldErrors) && <span className="field-error">{contractFieldErrors.replacement_days}</span>}
                                </div>

                                <div className="field">
                                    <label>Km Pickup <span className="required">*</span></label>
                                    <input
                                        value={contractData.km_pickup}
                                        placeholder="Ex: 95000"
                                        onChange={handleContractChange("km_pickup")}
                                        className={shouldShowError("km_pickup", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("km_pickup", contractFieldErrors) && <span className="field-error">{contractFieldErrors.km_pickup}</span>}
                                </div>

                                <div className="field">
                                    <label>Km Return <span className="required">*</span></label>
                                    <input
                                        value={contractData.km_return}
                                        placeholder="Ex: 95000"
                                        onChange={handleContractChange("km_return")}
                                        className={shouldShowError("km_return", contractFieldErrors) ? "error-input" : ""}
                                    />
                                    {shouldShowError("km_return", contractFieldErrors) && <span className="field-error">{contractFieldErrors.km_return}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddAutovehicles;